import { type JSX } from "react";
import clsx from "clsx";
import { Collapse } from "@/shared/ui";

interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  status?: "not started" | "in progress" | "completed";
}

interface RadioGroupProps {
  name?: string;
  options: RadioOption[];
  value: string | null;
  onChange: (value: string) => void;
  className?: string;
  showRadio?: boolean;
  direction?: "column" | "row";
  showDescription?: boolean;
  disabled?: boolean;
}

export function RadioGroup(props: RadioGroupProps): JSX.Element {
  const {
    name,
    options,
    value,
    onChange,
    className,
    showRadio,
    direction = "column",
    showDescription = true,
    disabled,
  } = props;

  return (
    <div className={clsx("flex gap-2", direction === "column" && "flex-col", className)}>
      {options.map((opt) => {
        const id = name ? `${name}-${opt.value}` : opt.value;
        const checked = value === opt.value;
        const isDisabled = disabled || opt.disabled;

        return (
          <label
            key={opt.value}
            htmlFor={id}
            className={clsx(
              "relative block w-full",
              !isDisabled && "cursor-pointer",
              isDisabled && "cursor-not-allowed",
            )}
          >
            <input
              id={id}
              type="radio"
              className="pointer-events-none absolute inset-0 opacity-0"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => onChange(opt.value)}
              disabled={isDisabled}
            />
            <div
              aria-hidden="true"
              className={clsx(
                "bg-background-grey flex items-center gap-4 rounded-[8px] border border-transparent px-6 py-5 transition-[background,box-shadow,transform,filter] duration-150 ease-out",
                !showRadio && checked && "bg-primary",
                !checked && "hover:bg-[#EEEEEE]",
                direction === "row" && "justify-center",
                isDisabled && "opacity-55",
              )}
            >
              {showRadio && (
                <span
                  className={clsx(
                    "border-eerie-black relative inline-block h-6 w-6 flex-[0_0_24px] rounded-full border bg-white",
                    "after:bg-eerie-black after:absolute after:inset-[6px] after:rounded-full after:content-['']",
                    "after:transition after:duration-150 after:ease-out",
                    checked ? "after:scale-100 after:opacity-100" : "after:scale-75 after:opacity-0",
                  )}
                />
              )}
              <div
                className={clsx(
                  "text-md flex w-full items-center justify-between font-bold",
                  !showRadio && checked ? "text-white" : "text-eerie-black",
                )}
              >
                <div>{opt.label}</div>
                {opt.status === "in progress" && <div className="uppercase">[DRAFT]</div>}
              </div>
            </div>
            {opt.description && (
              <Collapse open={!!showDescription}>
                <div className="text-eerie-black font-family-third mt-3 text-center text-xs">{opt.description}</div>
              </Collapse>
            )}
          </label>
        );
      })}
    </div>
  );
}
